import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  fetchPartnerDealerNormalizationReview,
  fetchPartnerDealers,
  fetchPartnerPortfolioNormalizationReview,
  normalizePartnerDealerListing,
} from "../../../services/automapPartnerDealerApi";
import { usePartnerPortalBase } from "../PartnerPortalBaseProvider";
import {
  PARTNER_NORMALIZATION_ISSUE_LABELS,
  PARTNER_NORMALIZATION_ISSUE_TYPES,
  PARTNER_NORMALIZATION_QUEUE_TABS,
  PARTNER_NORMALIZATION_SOURCE_FILTERS,
  PARTNER_NORMALIZATION_WORKFLOW_STEPS,
  partnerNormalizationIssueLabel,
  partnerNormalizationPrimaryIssueExplanation,
  partnerNormalizationQueueTab,
  partnerNormalizationStatusLabel,
} from "../../../constants/partnerNormalizationReviewConstants";
import "../../../styles/partnerDealers.css";

function formatPriceCop(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function formatMileage(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${new Intl.NumberFormat("es-CO").format(Number(value))} km`;
}

function formatConfidence(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `${Math.round(Number(value) * 100)}% confident`;
}

function formatIdentityValue(value) {
  return value?.trim() ? value.trim() : "—";
}

function WorkflowGuide() {
  return (
    <ol className="vehicle-name-fix__workflow">
      {PARTNER_NORMALIZATION_WORKFLOW_STEPS.map((step) => (
        <li key={step.step} className="vehicle-name-fix__workflow-step">
          <span className="vehicle-name-fix__workflow-number">{step.step}</span>
          <div>
            <strong>{step.title}</strong>
            <p>{step.detail}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function QueueTabs({ activeStatus, summary, onSelect }) {
  return (
    <div className="vehicle-name-fix__queue" role="tablist" aria-label="Review queue">
      {PARTNER_NORMALIZATION_QUEUE_TABS.map((tab) => {
        const count = summary?.[tab.summaryKey] ?? 0;
        const isActive = activeStatus === tab.value;
        return (
          <button
            key={tab.value || "default"}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`vehicle-name-fix__queue-tab${isActive ? " is-active" : ""}`}
            onClick={() => onSelect(tab.value)}
          >
            <span className="vehicle-name-fix__queue-tab-label">{tab.label}</span>
            <span className="vehicle-name-fix__queue-tab-count">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

function IdentityFieldRow({ label, dealerValue, matchedValue, isOptional = false, isMatched }) {
  const missing = !matchedValue || matchedValue === "—";
  const rowTone = missing && !isOptional ? "is-missing" : isMatched ? "is-ok" : "is-review";

  return (
    <div className={`vehicle-name-fix__field-row vehicle-name-fix__field-row--${rowTone}`}>
      <span className="vehicle-name-fix__field-label">{label}</span>
      <span className="vehicle-name-fix__field-dealer">{formatIdentityValue(dealerValue)}</span>
      <span className="vehicle-name-fix__field-arrow" aria-hidden="true">
        →
      </span>
      <span className="vehicle-name-fix__field-match">
        {formatIdentityValue(matchedValue)}
        {isOptional && missing ? <em className="vehicle-name-fix__optional">optional</em> : null}
      </span>
      <span className="vehicle-name-fix__field-status" aria-label={isMatched ? "Matched" : "Needs review"}>
        {isMatched ? "✓" : "!"}
      </span>
    </div>
  );
}

function IdentityComparison({ item }) {
  return (
    <div className="vehicle-name-fix__comparison">
      <div className="vehicle-name-fix__comparison-head">
        <span>Field</span>
        <span>Dealer wrote</span>
        <span aria-hidden="true" />
        <span>Our match</span>
        <span aria-hidden="true" />
      </div>
      <IdentityFieldRow
        label="Make"
        dealerValue={item.makeRaw}
        matchedValue={item.makeLabel}
        isMatched={Boolean(item.makeId && item.makeLabel)}
      />
      <IdentityFieldRow
        label="Model"
        dealerValue={item.modelRaw}
        matchedValue={item.modelLabel}
        isMatched={Boolean(item.modelId && item.modelLabel)}
      />
      <IdentityFieldRow
        label="Trim"
        dealerValue={item.trimRaw}
        matchedValue={item.trimLabel}
        isOptional
        isMatched={Boolean(item.trimId && item.trimLabel)}
      />
    </div>
  );
}

function ReviewCard({ item, path, onApplySuggestion, applyingListingId }) {
  const primarySuggestion = item.suggestions?.[0] || null;
  const isApplying = applyingListingId === item.listingId;
  const problemText = partnerNormalizationPrimaryIssueExplanation(item.issueTypes);

  const handleApplySuggestion = () => {
    if (!primarySuggestion || !item.dealerId) return;
    onApplySuggestion(item, primarySuggestion);
  };

  return (
    <article className="vehicle-name-fix__card">
      <header className="vehicle-name-fix__card-header">
        <div className="vehicle-name-fix__card-title-block">
          <h3>{item.title || "Untitled listing"}</h3>
          <p className="vehicle-name-fix__card-meta">
            {item.dealerName ? (
              <>
                <strong>{item.dealerName}</strong>
                <span aria-hidden="true"> · </span>
              </>
            ) : null}
            {item.year || "—"}
            <span aria-hidden="true"> · </span>
            {formatPriceCop(item.price)}
            <span aria-hidden="true"> · </span>
            {formatMileage(item.mileage)}
          </p>
        </div>
        <div className="vehicle-name-fix__card-badges">
          <span className="vehicle-name-fix__source-badge">
            {item.source === "live" ? "Imported" : "Portal"}
          </span>
          <span
            className={`vehicle-name-fix__status vehicle-name-fix__status--${item.normalizationStatus || "unknown"}`}
          >
            {partnerNormalizationStatusLabel(item.normalizationStatus)}
          </span>
        </div>
      </header>

      <div className="vehicle-name-fix__problem">
        <p className="vehicle-name-fix__problem-label">What needs attention</p>
        <p className="vehicle-name-fix__problem-text">{problemText}</p>
        {item.issueTypes?.length ? (
          <div className="vehicle-name-fix__issue-chips">
            {item.issueTypes.map((issueType) => (
              <span key={issueType} className="vehicle-name-fix__issue-chip">
                {partnerNormalizationIssueLabel(issueType)}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <IdentityComparison item={item} />

      {primarySuggestion ? (
        <div className="vehicle-name-fix__suggestion">
          <p className="vehicle-name-fix__suggestion-label">Suggested fix</p>
          <p className="vehicle-name-fix__suggestion-value">
            {[primarySuggestion.makeLabel, primarySuggestion.modelLabel, primarySuggestion.trimLabel]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p className="vehicle-name-fix__suggestion-reason">
            {formatConfidence(primarySuggestion.confidence)
              ? `${formatConfidence(primarySuggestion.confidence)} · `
              : ""}
            {primarySuggestion.reason || "Our catalog has a close match for this listing."}
          </p>
        </div>
      ) : (
        <div className="vehicle-name-fix__suggestion vehicle-name-fix__suggestion--empty">
          <p className="vehicle-name-fix__suggestion-label">Suggested fix</p>
          <p className="vehicle-name-fix__suggestion-reason">
            No automatic suggestion yet. Open the listing and pick the correct Make, Model, and Trim.
          </p>
        </div>
      )}

      <div className="vehicle-name-fix__actions">
        {primarySuggestion && item.dealerId ? (
          <button
            type="button"
            className="vehicle-name-fix__btn vehicle-name-fix__btn--primary"
            disabled={isApplying}
            onClick={handleApplySuggestion}
          >
            {isApplying ? "Applying…" : "Use suggested name"}
          </button>
        ) : null}
        {item.editPath ? (
          <Link className="vehicle-name-fix__btn" to={`${path(item.editPath)}?from=normalization`}>
            {primarySuggestion ? "Fix manually" : "Open listing to fix"}
          </Link>
        ) : null}
      </div>
    </article>
  );
}

export default function PartnerNormalizationReviewWorkspace({ dealerId = "" }) {
  const portfolioMode = !dealerId;
  const { path } = usePartnerPortalBase();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get("status") ?? "";
  const source = searchParams.get("source") || "";
  const issueType = searchParams.get("issueType") || "";
  const search = searchParams.get("search") || "";
  const dealerFilter = searchParams.get("dealerId") || "";

  const [data, setData] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [applyingListingId, setApplyingListingId] = useState("");

  const activeQueue = partnerNormalizationQueueTab(status);

  const loadReviewQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {
        status: status || undefined,
        source: source || undefined,
        issueType: issueType || undefined,
        search: search.trim() || undefined,
        sort: "updated_desc",
        limit: 50,
      };

      const result = portfolioMode
        ? await fetchPartnerPortfolioNormalizationReview({
            ...params,
            dealerId: dealerFilter || undefined,
          })
        : await fetchPartnerDealerNormalizationReview(dealerId, params);
      setData(result);
    } catch (err) {
      setError(err.message || "Unable to load vehicle name review queue.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealerFilter, dealerId, issueType, portfolioMode, search, source, status]);

  useEffect(() => {
    loadReviewQueue();
  }, [loadReviewQueue]);

  useEffect(() => {
    if (!portfolioMode) return undefined;
    let cancelled = false;

    fetchPartnerDealers({ sort: "dealer_name" })
      .then((result) => {
        if (!cancelled) setDealers(result.dealers || []);
      })
      .catch(() => {
        if (!cancelled) setDealers([]);
      });

    return () => {
      cancelled = true;
    };
  }, [portfolioMode]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params, { replace: true });
  };

  const handleApplySuggestion = async (item, suggestion) => {
    if (!item.dealerId) return;
    try {
      setApplyingListingId(item.listingId);
      setMessage("");
      setError("");
      await normalizePartnerDealerListing(item.dealerId, item.listingId, {
        makeId: suggestion.makeId,
        modelId: suggestion.modelId,
        trimId: suggestion.trimId ?? null,
        reason: "Accepted suggested vehicle name from review queue",
        source: "partner_review_queue",
      });
      setMessage(`Updated vehicle name for "${item.title || "listing"}".`);
      await loadReviewQueue();
    } catch (err) {
      setError(err.message || "Could not apply the suggested name.");
    } finally {
      setApplyingListingId("");
    }
  };

  const issueTypeOptions = useMemo(
    () =>
      Object.entries(PARTNER_NORMALIZATION_ISSUE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    []
  );

  const items = data?.items || [];
  const dealerName = data?.dealer?.businessName;
  const summary = data?.summary;
  const queueCount = summary?.[activeQueue.summaryKey] ?? items.length;

  return (
    <div className="vehicle-name-fix">
      <header className="vehicle-name-fix__hero">
        <div className="vehicle-name-fix__hero-copy">
          {portfolioMode ? (
            <Link className="vehicle-name-fix__back" to={path("inventory-health")}>
              ← Back to inventory health
            </Link>
          ) : (
            <div className="vehicle-name-fix__back-row">
              <Link className="vehicle-name-fix__back" to={path(`dealers/${dealerId}/inventory`)}>
                ← Back to inventory
              </Link>
              <Link className="vehicle-name-fix__back" to={path("inventory-health")}>
                Inventory health
              </Link>
            </div>
          )}
          <p className="vehicle-name-fix__eyebrow">Vehicle name matching</p>
          <h1>{portfolioMode ? "Fix vehicle names" : "Fix vehicle names"}</h1>
          <p className="vehicle-name-fix__lede">
            {portfolioMode ? (
              "Confirm the correct Make, Model, and Trim for each listing so vehicles group properly in search and on the map."
            ) : dealerName ? (
              <>
                Confirm vehicle names for <strong>{dealerName}</strong> so listings group correctly in
                search and inventory views.
              </>
            ) : (
              "Confirm the correct Make, Model, and Trim for listings that still need review."
            )}
          </p>
        </div>
        <WorkflowGuide />
      </header>

      {error ? <div className="vehicle-name-fix__alert vehicle-name-fix__alert--error">{error}</div> : null}
      {message ? (
        <div className="vehicle-name-fix__alert vehicle-name-fix__alert--success">{message}</div>
      ) : null}

      <section className="vehicle-name-fix__queue-panel">
        <div className="vehicle-name-fix__queue-intro">
          <h2>{activeQueue.label}</h2>
          <p>{activeQueue.description}</p>
          {!loading ? (
            <p className="vehicle-name-fix__queue-count">
              <strong>{queueCount}</strong> listing{queueCount === 1 ? "" : "s"} in this queue
            </p>
          ) : null}
        </div>
        <QueueTabs activeStatus={status} summary={summary} onSelect={(value) => updateFilter("status", value)} />
      </section>

      <div className="vehicle-name-fix__toolbar">
        <input
          type="search"
          className="vehicle-name-fix__search"
          placeholder="Search by make, model, trim, or title…"
          value={search}
          onChange={(event) => updateFilter("search", event.target.value)}
          aria-label="Search listings"
        />
        <select
          value={source}
          onChange={(event) => updateFilter("source", event.target.value)}
          aria-label="Filter by source"
        >
          {PARTNER_NORMALIZATION_SOURCE_FILTERS.map((option) => (
            <option key={option.value || "all"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={issueType}
          onChange={(event) => updateFilter("issueType", event.target.value)}
          aria-label="Filter by problem type"
        >
          <option value="">All problem types</option>
          {issueTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {portfolioMode ? (
          <select
            value={dealerFilter}
            onChange={(event) => updateFilter("dealerId", event.target.value)}
            aria-label="Filter by dealer"
          >
            <option value="">All dealers</option>
            {dealers.map((dealer) => (
              <option key={dealer.id} value={dealer.id}>
                {dealer.businessName}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {loading ? <p className="vehicle-name-fix__loading">Loading listings…</p> : null}

      {!loading && !error && items.length === 0 ? (
        <div className="vehicle-name-fix__empty">
          <h3>Nothing in this queue</h3>
          <p>
            {status === "matched"
              ? "No confirmed listings match your filters right now."
              : status === "all"
                ? "No listings match your filters."
                : "All vehicle names in this view look good. New imports will show up here when they need review."}
          </p>
        </div>
      ) : null}

      {!loading && items.length ? (
        <div className="vehicle-name-fix__list">
          {items.map((item) => (
            <ReviewCard
              key={`${item.dealerId || "dealer"}-${item.listingId}`}
              item={item}
              path={path}
              onApplySuggestion={handleApplySuggestion}
              applyingListingId={applyingListingId}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export { PARTNER_NORMALIZATION_ISSUE_TYPES };
